import { TSobReportSalesType } from 'legacy/lut/components/widgets/public/partner360/configs/reports'

import { Nullable } from 'cng-common/models/utils'
import { IQuarterlyExpectedExitFields } from 'partner360/models/report/sobAnalyticsReports/iQuarterlyMarketTableRow'
import { ISobAnalyticsReportJoined } from 'partner360/models/report/sobAnalyticsReports/ISobAnalyticsReportJoined'

import { calculateDiffPP } from './calculateDiffPP'
import { calculateSalesPerformance } from './calculateSalesPerformance'
import { calculateYoYGrowth } from './calculateYoYGrowth'
import { calculateSobAnalyticsForPeriod } from './sobAnalyticReport/weightedAverage/calculateSobAnalyticsForPeriod'

/** Expected Exit of one sell type and the figures the table derives from it */
export interface IExpectedExitFigures {
  revenue: Nullable<number>
  estimatedRevenue: Nullable<number>
  sob: Nullable<number>
  yoyPP: Nullable<number>
  qoq: Nullable<number>
  vsPlan: Nullable<number>
  salesPerformance: Nullable<number>
  yoyGrowth: Nullable<number>
  tam: Nullable<number>
  tamYoY: Nullable<number>
}

type TPeriodCalculation = ReturnType<typeof calculateSobAnalyticsForPeriod>

interface IExpectedExitParams {
  currYearData: ISobAnalyticsReportJoined[]
  quarters: number[]
  netNdp: TSobReportSalesType
  prevYear: TPeriodCalculation
  prevQuarter: TPeriodCalculation
  currYearPlan: TPeriodCalculation
}

// a row's actuals figures with the period's Expected Exit in place of its actuals
export const getExpectedExitFigures = ({
  currYearData,
  quarters,
  netNdp,
  prevYear,
  prevQuarter,
  currYearPlan,
}: IExpectedExitParams): IExpectedExitFigures => {
  const expectedExit = calculateSobAnalyticsForPeriod({
    data: currYearData,
    quarters,
    netNdp,
    isExpectedExit: true,
  })
  const sob = expectedExit.sob ?? expectedExit.plainSob

  return {
    revenue: expectedExit.actualsRevenue,
    estimatedRevenue: expectedExit.estimatedActualsRevenue,
    sob,
    yoyPP: calculateDiffPP({ sobActual: sob, sobPlan: prevYear.sob ?? prevYear.plainSob }),
    qoq: calculateDiffPP({ sobActual: sob, sobPlan: prevQuarter.sob ?? prevQuarter.plainSob }),
    vsPlan: calculateDiffPP({ sobActual: sob, sobPlan: currYearPlan.sob ?? currYearPlan.plainSob }),
    salesPerformance: calculateSalesPerformance({
      actualsRevenue: expectedExit.actualsRevenue,
      planRevenue: currYearPlan.actualsRevenue,
    }),
    yoyGrowth: calculateYoYGrowth({
      currYearActualsRevenue: expectedExit.actualsRevenue,
      prevYearActualsRevenue: prevYear.actualsRevenue,
    }),
    tam: expectedExit.tam,
    tamYoY:
      prevYear.tam && expectedExit.tam
        ? ((expectedExit.tam - prevYear.tam) / prevYear.tam) * 100
        : null,
  }
}

export const getExpectedExitRowFields = ({
  Ndp,
  Net,
  TNet,
  UTNet,
}: Record<TSobReportSalesType, IExpectedExitFigures>): IQuarterlyExpectedExitFields => ({
  currYearExpExitRevenue: Ndp.revenue,
  currYearExpExitRevenueNet: Net.revenue,
  currYearExpExitRevenueTNet: TNet.revenue,
  currYearExpExitRevenueUTNet: UTNet.revenue,

  currYearEstimatedExpExitRevenue: Ndp.estimatedRevenue,
  currYearEstimatedExpExitRevenueNet: Net.estimatedRevenue,
  currYearEstimatedExpExitRevenueTNet: TNet.estimatedRevenue,
  currYearEstimatedExpExitRevenueUTNet: UTNet.estimatedRevenue,

  currYearSobExpExit: Ndp.sob,
  currYearSobExpExitNet: Net.sob,
  currYearSobExpExitTNet: TNet.sob,
  currYearSobExpExitUTNet: UTNet.sob,

  expExitYoYPP: Ndp.yoyPP,
  expExitYoYPPNet: Net.yoyPP,
  expExitYoYPPTNet: TNet.yoyPP,
  expExitYoYPPUTNet: UTNet.yoyPP,

  expExitQoQ: Ndp.qoq,
  expExitQoQNet: Net.qoq,
  expExitQoQTNet: TNet.qoq,
  expExitQoQUTNet: UTNet.qoq,

  expExitVsPlan: Ndp.vsPlan,
  expExitVsPlanNet: Net.vsPlan,
  expExitVsPlanTNet: TNet.vsPlan,
  expExitVsPlanUTNet: UTNet.vsPlan,

  expExitSalesPerformance: Ndp.salesPerformance,
  expExitSalesPerformanceNet: Net.salesPerformance,
  expExitSalesPerformanceTNet: TNet.salesPerformance,
  expExitSalesPerformanceUTNet: UTNet.salesPerformance,

  expExitYoYGrowth: Ndp.yoyGrowth,
  expExitYoYGrowthNet: Net.yoyGrowth,
  expExitYoYGrowthTNet: TNet.yoyGrowth,
  expExitYoYGrowthUTNet: UTNet.yoyGrowth,

  currYearQuarterTamExpExit: Ndp.tam,
  currYearQuarterTamExpExitNet: Net.tam,
  currYearQuarterTamExpExitTNet: TNet.tam,
  currYearQuarterTamExpExitUTNet: UTNet.tam,

  partnerTamExpExitYoY: Ndp.tamYoY,
  partnerTamExpExitYoYNet: Net.tamYoY,
  partnerTamExpExitYoYTNet: TNet.tamYoY,
  partnerTamExpExitYoYUTNet: UTNet.tamYoY,
})
